import { useState } from 'react';

import { Box, Flex, VStack } from '@chakra-ui/react';
import { Add, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@mesh.ai/rabbit';
import CategoryMultiSelectWrapper from 'Deer/components/CategoryMultiSelect/CategoryMultiSelectWrapper';
import { useDispatch } from 'react-redux';
import { resetView, resetViewsPanel } from 'reducers/viewsSlice';
import { LabelFieldWrapper } from 'screens/admin/Reviews/ReviewProcessV2Intent/components/LabelFieldWrapper';
import { checkStaticDataCompensationEnabled } from 'utils/helpers/featureFlags';

import { ArrowCircleUp, InfoWorkZero } from 'assets/assetsV1/iconComponents';
import { ArrowDropRight } from 'assets/assetsV1/iconComponents/ArrowDropRight';

import { PARTICIPANTS_SELECTION_MODULES } from 'constants/ProjectConstants';

import { BottomDrawer, DrawerBody, DrawerContent, DrawerFooter, DrawerHeader } from 'uiToolkitV1/BottomDrawer';
import { Button } from 'uiToolkitV1/Button';
import { ListItemComp } from 'uiToolkitV1/ListItemComp';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { useUpdateStakeholderMap, useUpdateStakeholderRelation } from '../dataHooks';
import { StakeHolderFlatListTable } from './StakeHolderFlatListTable';
import { StakeHolderTreeTable } from './StakeHolderTreeTable';
import { TABLE_VIEW_TYPES, TableViewType } from './shared/stakeholderConstants';
import { closeWorkflowStakeholdersDrawer, useCompensationDrawersState } from './slice/compensationDrawersSlice';

// Mock data for dropdowns
const workflowOptions = [
  {
    value: 'propose_approve_publish',
    label: 'Propose > Approve > Publish',
    workflow: ['Propose', 'Approve', 'Publish'],
  },
  { value: 'propose_publish', label: 'Propose > Publish', workflow: ['Propose', 'Publish'] },
  { value: 'admin_only_publish', label: 'Admin-only Publish', workflow: ['Admin-only Publish'] },
  {
    value: 'propose_auto_approve',
    label: 'Propose > Auto-approve (within budget and guidelines) > Publish',
    workflow: ['Propose', 'Auto-approve (within budget and guidelines)', 'Publish'],
  },
];

// Helper function to check if a stakeholder column has changed
const hasStakeholderColumnChanged = (
  modifiedRow: any,
  originalRow: any,
  columnKey: string,
  selectedEmployeesKey: string
): boolean => {
  // Check if the main column value has changed
  if (modifiedRow[columnKey] !== originalRow[columnKey]) {
    return true;
  }

  // Check if selected employees have changed
  const modifiedEmployees = modifiedRow[selectedEmployeesKey];
  const originalEmployees = originalRow[selectedEmployeesKey];

  // If both are null/undefined, no change
  if (!modifiedEmployees && !originalEmployees) {
    return false;
  }

  // If one is null/undefined and the other isn't, there's a change
  if (!modifiedEmployees || !originalEmployees) {
    return true;
  }

  // If arrays have different lengths, there's a change
  if (modifiedEmployees.length !== originalEmployees.length) {
    return true;
  }

  // Compare each employee in the arrays
  const res = modifiedEmployees.some((modifiedEmp: any, index: number) => {
    const originalEmp = originalEmployees[index];

    return modifiedEmp.id !== originalEmp.id;
  });

  return res;
};

// Helper function to get changed rows
const getChangedRows = (modifiedRows: any[], originalRows: any[]) =>
  modifiedRows.filter((modifiedRow) => {
    // Find the corresponding original row by id
    const originalRow = originalRows.find((row) => row.id === modifiedRow.id);

    if (!originalRow) {
      // If no original row found, consider it as changed
      return true;
    }

    // Check if any of the three editable columns have changed
    const hasProposerChanged = hasStakeholderColumnChanged(
      modifiedRow,
      originalRow,
      'PROPOSER',
      'proposerSelectedEmployees'
    );
    const hasApproverChanged = hasStakeholderColumnChanged(
      modifiedRow,
      originalRow,
      'APPROVER',
      'approverSelectedEmployees'
    );
    const hasPublisherChanged = hasStakeholderColumnChanged(
      modifiedRow,
      originalRow,
      'PUBLISHER',
      'publisherSelectedEmployees'
    );

    return hasProposerChanged || hasApproverChanged || hasPublisherChanged;
  });

export const WorkflowStakeholdersDrawer = () => {
  const { isOpen } = useCompensationDrawersState();
  const dispatch = useDispatch();

  const [selectedWorkflow, setSelectedWorkflow] = useState(workflowOptions[0]);
  const [tableView, setTableView] = useState<TableViewType>(TABLE_VIEW_TYPES.FUNCTION_TREE);
  const isEmployeeList = tableView === TABLE_VIEW_TYPES.FLAT_LIST;

  const onClose = () => {
    dispatch(closeWorkflowStakeholdersDrawer());
    dispatch(resetViewsPanel());
    dispatch(resetView());
  };

  const {
    modifiedStakeholdersTreeRows,
    originalStakeholdersTreeRows,
    modifiedStakeholdersRows,
    originalStakeholdersRows,
  } = useCompensationDrawersState();

  const compensationId = 1; // remove the mock id after integration

  const { mutate: updateStakeholderRelationMutation } = useUpdateStakeholderRelation({
    compensationId,
    onSuccess: () => setTableView(TABLE_VIEW_TYPES.FLAT_LIST),
  });

  const { mutate: updateStakeholderMapMutation } = useUpdateStakeholderMap({
    compensationId,
    onSuccess: () => onClose(),
  });

  const onNext = () => {
    if (checkStaticDataCompensationEnabled()) {
      setTableView(TABLE_VIEW_TYPES.FLAT_LIST);
      return;
    }

    const changedRows = getChangedRows(modifiedStakeholdersTreeRows, originalStakeholdersTreeRows);

    if (changedRows.length === 0) {
      setTableView(TABLE_VIEW_TYPES.FLAT_LIST);
      return;
    }

    const stakeholderMappingsPayload = modifiedStakeholdersTreeRows.map((row) => ({
      jobFunctionId: row.id,
      stakeholderAssignments: {
        PUBLISHER: {
          employeeIds:
            row.PUBLISHER === 'ANY_EMPLOYEE' && row.publisherSelectedEmployees?.length > 0
              ? row.publisherSelectedEmployees?.map((employee) => employee.id)
              : [],
          stakeholderRelation: row.PUBLISHER === 'ANY_EMPLOYEE' ? null : row.PUBLISHER,
        },
        PROPOSER: {
          employeeIds:
            row.PROPOSER === 'ANY_EMPLOYEE' && row.proposerSelectedEmployees?.length > 0
              ? row.proposerSelectedEmployees?.map((employee) => employee.id)
              : [],
          stakeholderRelation: row.PROPOSER === 'ANY_EMPLOYEE' ? null : row.PROPOSER,
        },
        APPROVER: {
          employeeIds:
            row.APPROVER === 'ANY_EMPLOYEE' && row.approverSelectedEmployees?.length > 0
              ? row.approverSelectedEmployees?.map((employee) => employee.id)
              : [],
          stakeholderRelation: row.APPROVER === 'ANY_EMPLOYEE' ? null : row.APPROVER,
        },
      },
    }));

    updateStakeholderRelationMutation({
      stakeholderMappings: stakeholderMappingsPayload,
    });
  };

  const onSave = () => {
    if (checkStaticDataCompensationEnabled()) {
      onClose();
      return;
    }

    const changedRows = getChangedRows(modifiedStakeholdersRows, originalStakeholdersRows);

    if (changedRows.length > 0) {
      updateStakeholderMapMutation({
        stakeholderMappings: changedRows.map((row) => ({
          employeeId: row.id,
          stakeholderAssignments: {
            PUBLISHER:
              row.PUBLISHER === 'ANY_EMPLOYEE' && row.publisherSelectedEmployees?.length > 0
                ? row.publisherSelectedEmployees?.map((employee) => employee.id)
                : [],
            PROPOSER:
              row.PROPOSER === 'ANY_EMPLOYEE' && row.proposerSelectedEmployees?.length > 0
                ? row.proposerSelectedEmployees?.map((employee) => employee.id)
                : [],
            APPROVER:
              row.APPROVER === 'ANY_EMPLOYEE' && row.approverSelectedEmployees?.length > 0
                ? row.approverSelectedEmployees?.map((employee) => employee.id)
                : [],
          },
        })),
      });
    }
  };

  return (
    <BottomDrawer isOpen={isOpen} onClose={onClose} placement="bottom">
      <DrawerContent width="auto" ml="40px !important" maxH="calc(100vh - 40px)">
        <DrawerHeader onClose={onClose}>
          <ListItemComp title="Workflow stakeholders" withoutBackground padding="16px 24px" />
        </DrawerHeader>
        <DrawerBody>
          <Box
            borderWidth="4px"
            borderStyle="solid"
            borderColor="neutral.EBEFF3"
            padding="8px"
            margin="0 8px 8px 8px"
            borderRadius="8px"
            overflowY="auto"
          >
            <VStack spacing={6} align="stretch">
              <LabelFieldWrapper label="Workflow options " gridTemplateColumns="320px 1fr" gridGap={0}>
                <Box width="500px" sx={{ button: { width: '100%' } }}>
                  <Select
                    value={selectedWorkflow.value}
                    onValueChange={(value) => {
                      const selectedOption = workflowOptions.find((option) => option.value === value);
                      if (selectedOption) {
                        setSelectedWorkflow(selectedOption);
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue>
                        {selectedWorkflow && (
                          <Flex align="center" gap="2px">
                            {selectedWorkflow.workflow.map((step, index) => (
                              <>
                                <TextBodyMedium key={step}>{step}</TextBodyMedium>
                                {index !== selectedWorkflow.workflow.length - 1 && <ArrowDropRight boxSize="16px" />}
                              </>
                            ))}
                          </Flex>
                        )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent style={{ minWidth: 'fit-content' }}>
                      {workflowOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          <Flex align="center" gap="2px">
                            {option.workflow.map((step, index) => (
                              <>
                                <TextBodyMedium key={step}>{step}</TextBodyMedium>
                                {index !== option.workflow.length - 1 && <ArrowDropRight boxSize="16px" />}
                              </>
                            ))}
                          </Flex>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Box>
              </LabelFieldWrapper>

              <LabelFieldWrapper label="Employee selection " gridTemplateColumns="320px 1fr" gridGap={0}>
                <Box width="500px">
                  <CategoryMultiSelectWrapper
                    metaData={{
                      totalEmployeeCount: 400,
                      revieweeSelectionType: 'ALL_EMPLOYEES',
                      selectedFilters: null,
                      reviewCycleId: null,
                    }}
                    setMetaData={() => {}}
                    module={PARTICIPANTS_SELECTION_MODULES.REVIEW}
                  />
                </Box>
              </LabelFieldWrapper>
            </VStack>
            <Box
              display="flex"
              p="12px"
              flexDirection="column"
              alignItems="flex-start"
              gap="12px"
              alignSelf="stretch"
              borderRadius="8px"
              border="1px solid #F5F7F9"
              bg="#FDFDFD"
            >
              <Flex justify="space-between" align="flex-start" width="100%">
                <Box display="flex" flexDirection="column" gap="8px">
                  <TextLabel bold>Compensation stakeholders</TextLabel>

                  <Flex gap="4px" alignItems="center">
                    <InfoWorkZero />
                    <TextBodyMedium fontSize="12px">
                      Compensation won’t show with the review form if the proposer isn’t the manager. They’ll be
                      notified separately.
                    </TextBodyMedium>
                  </Flex>
                </Box>

                <Flex gap="8px">
                  <Button variant="outlined" StartIcon={Add} h="32px !important">
                    Add
                  </Button>
                  <Button variant="tertiary" StartIcon={ArrowCircleUp} h="32px !important">
                    Upload
                  </Button>
                </Flex>
              </Flex>
              {tableView === TABLE_VIEW_TYPES.FLAT_LIST && (
                <StakeHolderFlatListTable gridStatePersistenceKey="stakeholder-flat-list-table" />
              )}
              {tableView === TABLE_VIEW_TYPES.FUNCTION_TREE && (
                <StakeHolderTreeTable gridStatePersistenceKey="stakeholder-tree-table" />
              )}
            </Box>
          </Box>
        </DrawerBody>

        <DrawerFooter display="flex" justifyContent="space-between">
          {!isEmployeeList && (
            <>
              <Button variant="outlined" mr={4} onClick={onClose}>
                Cancel
              </Button>
              <Button variant="filled" colorScheme="blue" onClick={onNext}>
                Next
              </Button>
            </>
          )}
          {isEmployeeList && (
            <>
              <Button variant="tertiary" onClick={() => setTableView(TABLE_VIEW_TYPES.FUNCTION_TREE)}>
                Back
              </Button>
              <Box>
                <Button variant="outlined" mr={4} onClick={onClose}>
                  Cancel
                </Button>
                <Button variant="filled" colorScheme="blue" onClick={onSave}>
                  Save
                </Button>
              </Box>
            </>
          )}
        </DrawerFooter>
      </DrawerContent>
    </BottomDrawer>
  );
};
